import type { Review } from '../../data'
import ReviewStars from './review-stars'
import { ReviewAttribution } from './review-attribution'
import { displayTitle } from '../../helpers/utils'
import { Home } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../../components/ui/tooltip'
import { useReviewsConfig } from '../../providers'
import { ReviewPropertyLink } from './review-property-link'

export function ReviewCard({ review }: { review: Review }) {
  const options = useReviewsConfig()

  const isTruncate = typeof options.maxFeedbackLength === 'number' && options.maxFeedbackLength > 0
  const feedback = review.content || ''
  const truncated =
    isTruncate && feedback.length > options.maxFeedbackLength
      ? feedback.slice(0, options.maxFeedbackLength) + '...'
      : feedback
  const showReadMore =
    options.popupForDetail && isTruncate && feedback.length > options.maxFeedbackLength && options.onReadMore
  const title = displayTitle(review)
  const showProperty = Boolean(options.showPropertyName && review.property_name)
  // #1692: the property name carries the link; with the property line hidden
  // the title carries it instead (toolkit: footer link, else the card).
  const linkProperty = Boolean(options.linkToProperty)
  const linkTitle = linkProperty && !showProperty

  return (
    <div className="homerunner-review-card bg-card text-card-foreground rounded-xl shadow-sm border border-border p-5 flex flex-col h-full min-h-[180px]">
      <ReviewAttribution review={review} className="homerunner-review-card-header mb-3" />
      {!options.hideRating && (
        <div className="homerunner-review-card-rating mb-2">
          <div className="homerunner-review-card-stars">
            <ReviewStars rating={review.rating} starClass="w-4 h-4" />
          </div>
        </div>
      )}
      {title && (
        <span className="homerunner-review-card-title font-bold mb-2 text-foreground">
          {linkTitle ? <ReviewPropertyLink review={review} className="homerunner-review-title-link">{title}</ReviewPropertyLink> : title}
        </span>
      )}
      {showProperty && (
        <div className="homerunner-review-card-property flex items-center gap-2 mb-2 text-base font-medium text-foreground">
          <Home className="homerunner-review-card-property-icon inline w-5 h-5 mr-1" />
          <span className="homerunner-review-card-property-label">
            Stayed at
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="homerunner-review-card-property-name font-bold underline max-w-[180px] truncate inline-block align-bottom ml-1 cursor-pointer text-accent">
                    {linkProperty ? <ReviewPropertyLink review={review} className="homerunner-review-property-link">{review.property_name}</ReviewPropertyLink> : review.property_name}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {review.property_name}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </span>
        </div>
      )}
      {!options.hideFeedback && (
        <div className="homerunner-review-card-feedback text-base text-foreground font-normal flex-1">
          {isTruncate ? truncated : feedback}
          {showReadMore && (
            <button
              className="homerunner-review-card-read-more ml-2 text-accent underline text-base font-medium"
              onClick={() => options.onReadMore?.(review)}
              type="button"
              tabIndex={-1}
            >
              More
            </button>
          )}
        </div>
      )}
    </div>
  )
}
